/**
 * Collateral token metadata and USD price resolution.
 *
 * Three pricing tiers:
 *   1. Known 1:1 USD stablecoins — USDC, DAI (mainnet/testnet) → price = 1.0
 *   2. Any other ERC-20 — router getAmountsOut([token, USDC]) path to derive USD price
 *   3. Fallback: if router call fails, price = null and UI should hide $ formatting
 *
 * Results are cached per token address for CACHE_TTL_MS.
 */

import type { Address } from 'viem';
import { readClient } from './client';
import { erc20Abi } from './abi';
import { getActiveNetworkKey, NETWORK_ROUTERS, NETWORK_STABLE_ADDRESSES, NETWORK_USDC } from './config';
import { getCache, setCache } from './cache';
import { getTokenByAddress, getTokenByAddressSync, getTokenLogoUri, getTokenLogoUriSync } from './tokenList';

export type CollateralMeta = {
  address: string;
  name: string;
  symbol: string;
  decimals: number;
  /** USD price multiplier: 1 unit of token == usdPrice USD. null means unknown. */
  usdPrice: number | null;
  /** true when token is a known 1:1 USD stable, false when router-priced, null when unresolved */
  isStable: boolean;
};

const CACHE_TTL_MS = 60_000;

/** Minimal UniswapV2-compatible router ABI for price discovery. */
const routerAbi = [
  {
    name: 'getAmountsOut',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'amountIn', type: 'uint256' },
      { name: 'path', type: 'address[]' },
    ],
    outputs: [{ name: 'amounts', type: 'uint256[]' }],
  },
] as const;

/**
 * Try to quote 1 whole unit of `tokenAddress` in USDC terms by walking the
 * configured router list for the active network.  Returns null if no router
 * succeeds.
 */
async function quoteTokenInUsdc(
  tokenAddress: string,
  tokenDecimals: number,
): Promise<number | null> {
  const networkKey = getActiveNetworkKey();
  const routers = NETWORK_ROUTERS[networkKey] ?? [];
  const usdc = NETWORK_USDC[networkKey];
  if (!usdc || routers.length === 0) return null;

  const amountIn = BigInt(10 ** tokenDecimals); // 1 whole token
  const path = [tokenAddress as Address, usdc as Address];

  for (const routerAddress of routers) {
    try {
      const amounts = (await readClient.readContract({
        address: routerAddress as Address,
        abi: routerAbi,
        functionName: 'getAmountsOut',
        args: [amountIn, path],
      })) as bigint[];
      if (amounts && amounts.length >= 2 && amounts[1] > 0n) {
        // USDC is 6 decimals → divide by 1e6 to get USD price
        return Number(amounts[1]) / 1_000_000;
      }
    } catch {
      // Try next router
    }
  }
  return null;
}

/**
 * Resolve full metadata for a collateral token.
 * Results are cached per (network + address).
 */
export async function resolveCollateralMeta(tokenAddress: string): Promise<CollateralMeta> {
  const networkKey = getActiveNetworkKey();
  const cacheKey = `collateral:${networkKey}:${tokenAddress.toLowerCase()}`;
  const cached = getCache<CollateralMeta>(cacheKey, CACHE_TTL_MS);
  if (cached) return cached;

  const stableSet = NETWORK_STABLE_ADDRESSES[networkKey] ?? new Set<string>();
  const isStable = stableSet.has(tokenAddress.toLowerCase());

  // Read symbol + decimals from chain
  let name = 'Collateral Token';
  let symbol = 'TOKEN';
  let decimals = 18;
  try {
    const [rawName, rawSymbol, rawDecimals] = await Promise.all([
      readClient.readContract({
        address: tokenAddress as Address,
        abi: erc20Abi,
        functionName: 'name',
      }),
      readClient.readContract({
        address: tokenAddress as Address,
        abi: erc20Abi,
        functionName: 'symbol',
      }),
      readClient.readContract({
        address: tokenAddress as Address,
        abi: erc20Abi,
        functionName: 'decimals',
      }),
    ]);
    name = sanitizeName(String(rawName));
    symbol = sanitizeSymbol(String(rawSymbol));
    decimals = Number(rawDecimals);
  } catch {
    // fallback to defaults
  }

  const meta: CollateralMeta = {
    address: tokenAddress,
    name,
    symbol,
    decimals,
    usdPrice: isStable ? 1.0 : null,
    isStable,
  };
  setCache(cacheKey, meta);

  // Keep initial metadata resolution fast for UI rendering; price discovery can
  // happen in the background and hydrate cache for subsequent refresh cycles.
  if (!isStable) {
    void quoteTokenInUsdc(tokenAddress, decimals).then((quotedUsdPrice) => {
      if (quotedUsdPrice === null) return;
      setCache(cacheKey, { ...meta, usdPrice: quotedUsdPrice });
    });
  }

  return meta;
}

/**
 * Format a raw collateral bigint amount as a human-readable string.
 * Uses the collateral's own decimals, not the hardcoded 6.
 */
export function fmtCollateral(amount: bigint, meta: CollateralMeta): string {
  const n = Number(amount) / 10 ** meta.decimals;
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/**
 * Convert a raw collateral bigint to a USD-equivalent value string.
 * Returns null if the USD price is unknown.
 */
export function fmtCollateralUsd(amount: bigint, meta: CollateralMeta): string | null {
  if (meta.usdPrice === null) return null;
  const n = (Number(amount) / 10 ** meta.decimals) * meta.usdPrice;
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function sanitizeSymbol(raw: string): string {
  return raw.replace(/[^A-Za-z0-9.\-_]/g, '').slice(0, 12) || 'TOKEN';
}

function sanitizeName(raw: string): string {
  const trimmed = raw.replace(/\s+/g, ' ').trim();
  if (!trimmed) return 'Collateral Token';
  return trimmed.slice(0, 64);
}

/**
 * Blank/default meta used before a real resolve completes.
 * Stable tokens default to 6; unresolved custom tokens default to 18.
 */
export function defaultCollateralMeta(address: string): CollateralMeta {
  const networkKey = getActiveNetworkKey();
  const stableSet = NETWORK_STABLE_ADDRESSES[networkKey] ?? new Set<string>();
  const isStable = stableSet.has(address.toLowerCase());
  return {
    address,
    name: isStable ? 'USD Coin' : 'Collateral Token',
    symbol: isStable ? 'USDC' : 'TOKEN',
    decimals: isStable ? 6 : 18,
    usdPrice: isStable ? 1.0 : null,
    isStable,
  };
}

/**
 * Well-known local logos bundled with the app (served from /boxbet/images/).
 * Used as a fast synchronous fallback before the token list loads.
 */
const LOCAL_LOGO_USDC_MAINNET = new URL('../image/usdc.png', import.meta.url).href;
const LOCAL_LOGO_USDC_TESTNET = new URL('../image/usdc.png', import.meta.url).href;
const LOCAL_LOGO_DAI_MAINNET = new URL('../image/0xefD766cCb38EaF1dfd701853BFCe31359239F305.png', import.meta.url).href;
const LOCAL_LOGO_DAI_TESTNET = new URL('../image/0x826e4e896CC2f5B371Cd7Bb0bd929DB3e3DB67c0.png', import.meta.url).href;
const LOCAL_LOGO_PLS = new URL('../image/pls.png', import.meta.url).href;
const LOCAL_LOGO_COIN = new URL('../image/coin.svg', import.meta.url).href;
const logoValidationCache = new Map<string, Promise<string>>();

function isPlsWrapperSymbol(symbol: string | null | undefined): boolean {
  const normalized = String(symbol || '').trim().toUpperCase();
  return normalized === 'WPLS' || normalized === 'TPLS' || normalized === 'PLS';
}

const LOCAL_LOGOS: Record<string, string> = {
  '0x15d38573d2feeb82e7ad5187ab8c1d52810b1f07': LOCAL_LOGO_USDC_MAINNET,
  '0x7007f459d5334b35b7a56147915cb08765d4cdc4': LOCAL_LOGO_USDC_TESTNET,
  '0xefd766ccb38eaf1dfd701853bfce31359239f305': LOCAL_LOGO_DAI_MAINNET,
  '0x826e4e896cc2f5b371cd7bb0bd929db3e3db67c0': LOCAL_LOGO_DAI_TESTNET,
  '0xa1077a294dde1b09bb078844df40758a5d0f9a27': LOCAL_LOGO_PLS,
};

function isBundledLogo(url: string): boolean {
  return url.startsWith('data:') || url.startsWith('blob:') || url.includes('/assets/') || url.includes('/images/');
}

async function ensureLogoUrl(url: string | null): Promise<string> {
  const candidate = String(url || '').trim();
  if (!candidate) return LOCAL_LOGO_COIN;
  if (candidate === LOCAL_LOGO_COIN || isBundledLogo(candidate) || typeof Image === 'undefined') return candidate;

  const cached = logoValidationCache.get(candidate);
  if (cached) return cached;

  const probePromise = new Promise<string>((resolve) => {
    const image = new Image();
    let settled = false;
    const done = (value: string) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    image.onload = () => done(candidate);
    image.onerror = () => done(LOCAL_LOGO_COIN);
    image.src = candidate;
    window.setTimeout(() => done(LOCAL_LOGO_COIN), 2500);
  });

  logoValidationCache.set(candidate, probePromise);
  return probePromise;
}

/**
 * Resolve a display logo URL for a token address.
 * Returns local bundled image first, then token-list URI, then null.
 */
export async function resolveTokenLogoUrl(address: string): Promise<string | null> {
  const lower = address.toLowerCase();
  if (LOCAL_LOGOS[lower]) return LOCAL_LOGOS[lower];
  const token = await getTokenByAddress(address);
  if (isPlsWrapperSymbol(token?.symbol)) return LOCAL_LOGO_PLS;
  const listLogo = await getTokenLogoUri(address);
  return ensureLogoUrl(listLogo);
}

/**
 * Synchronous version — returns local logo or token-list cache hit, else null.
 */
export function resolveTokenLogoUrlSync(address: string): string | null {
  const lower = address.toLowerCase();
  if (LOCAL_LOGOS[lower]) return LOCAL_LOGOS[lower];
  const token = getTokenByAddressSync(address);
  if (isPlsWrapperSymbol(token?.symbol)) return LOCAL_LOGO_PLS;
  const listLogo = getTokenLogoUriSync(address);
  return listLogo && listLogo.trim().length > 0 ? listLogo : LOCAL_LOGO_COIN;
}
